import React from 'react';
import { useShowStore } from '../stores/showStore';
import { computeCueNumbers } from '../lib/cueNumbering';
import {
  computeInTimes,
  selectOnAirCue,
  selectNextCue,
  deriveCueStatus,
} from '../lib/rundownDerive';

// Single shared derivation for every rundown surface. The standard table and the
// modern spine BOTH consume this — no forked status/drift/in-time logic. Returns
// the raw memoised pieces (used by RundownTable's existing render loop and DnD)
// plus a normalised `rows` view-model array over the visible cues that the
// modern spine renders directly. Live per-second values (drift, countdowns) are
// intentionally NOT baked in here — the cells subscribe to ClockTickContext so
// this object only changes when the underlying data changes, not every tick.
export function useRundownRows(show) {
  const cues = useShowStore(s => s.cues);
  const collapsedGroups = useShowStore(s => s.collapsedGroups);
  const collapsingGroups = useShowStore(s => s.collapsingGroups);
  const expandingGroups = useShowStore(s => s.expandingGroups);

  const onAirCue = React.useMemo(() => selectOnAirCue(cues), [cues]);
  const nextCue = React.useMemo(() => selectNextCue(cues), [cues]);

  // Cues visible in the list — children of collapsed groups are hidden, but kept
  // visible while their group is mid-collapse animation.
  const displayedCues = React.useMemo(
    () => cues.filter(c => !c.parent_id || !collapsedGroups.has(c.parent_id) || collapsingGroups.has(c.parent_id)),
    [cues, collapsedGroups, collapsingGroups]
  );

  // parentId → sorted children (all cues, including hidden ones).
  const childrenByParent = React.useMemo(() => {
    const map = new Map();
    cues.forEach(c => {
      if (c.parent_id) {
        if (!map.has(c.parent_id)) map.set(c.parent_id, []);
        map.get(c.parent_id).push(c);
      }
    });
    map.forEach(arr => arr.sort((a, b) => a.order - b.order));
    return map;
  }, [cues]);

  // Script-style hierarchical display numbers (1, 2, 2.1, 2.2, 3…).
  const cueNumbers = React.useMemo(() => computeCueNumbers(cues), [cues]);

  // Sum of children durations for each GROUP.
  const groupDurations = React.useMemo(() => {
    const map = new Map();
    childrenByParent.forEach((children, parentId) => {
      map.set(parentId, children.reduce((sum, c) => sum + (c.planned_duration || 0), 0));
    });
    return map;
  }, [childrenByParent]);

  const showStartIdx = React.useMemo(
    () => cues.findIndex(c => c.cue_type === 'SHOW_START'),
    [cues]
  );

  const inTimes = React.useMemo(
    () => computeInTimes(cues, show),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cues, show?.show_start_time, show?.scheduled_start]
  );

  // Global cue index by id — avoids repeated O(n) findIndex in render loops.
  const globalIndexById = React.useMemo(() => {
    const m = new Map();
    cues.forEach((c, i) => m.set(c.id, i));
    return m;
  }, [cues]);

  const inTimeById = React.useMemo(() => {
    const m = new Map();
    cues.forEach((c, i) => m.set(c.id, inTimes[i]));
    return m;
  }, [cues, inTimes]);

  // Normalised per-row view models over the VISIBLE cues, in display order.
  // rows[i] corresponds to displayedCues[i], so a virtualiser keyed on
  // displayedCues can look up its row model by the same index.
  const rows = React.useMemo(() => {
    return displayedCues.map((cue, displayIndex) => {
      const globalIndex = globalIndexById.get(cue.id) ?? -1;
      const isOnAir = cue.id === onAirCue?.id;
      const isNext = cue.id === nextCue?.id;
      const isShowStart = cue.cue_type === 'SHOW_START';
      const isGroupParent = cue.cue_type === 'GROUP';
      const isChild = !!cue.parent_id;
      const isPreShow = showStartIdx !== -1 && globalIndex < showStartIdx;
      const inTime = inTimes[globalIndex] ?? null;
      const plannedDuration = isGroupParent
        ? (groupDurations.get(cue.id) ?? 0)
        : (cue.planned_duration || 0);
      const outTime = inTime ? new Date(inTime.getTime() + plannedDuration * 1000) : null;
      return {
        cue,
        id: cue.id,
        globalIndex,
        displayIndex,
        displayNumber: cueNumbers.get(cue.id),
        status: deriveCueStatus(cue, { isOnAir, isNext, isShowStart }),
        isOnAir,
        isNext,
        isShowStart,
        isGroupParent,
        isChild,
        isPreShow,
        isGroupCollapsed: collapsedGroups.has(cue.id),
        isCollapsing: isChild && collapsingGroups.has(cue.parent_id),
        isExpanding: isChild && expandingGroups.has(cue.parent_id),
        childrenCount: isGroupParent ? (childrenByParent.get(cue.id) || []).length : 0,
        inTime,
        outTime,
        plannedDuration,
        groupDuration: isGroupParent ? plannedDuration : undefined,
      };
    });
  }, [
    displayedCues, globalIndexById, onAirCue?.id, nextCue?.id, showStartIdx,
    inTimes, groupDurations, cueNumbers, collapsedGroups, collapsingGroups, expandingGroups, childrenByParent,
  ]);

  return {
    cues,
    displayedCues,
    childrenByParent,
    cueNumbers,
    groupDurations,
    onAirCue,
    nextCue,
    inTimes,
    inTimeById,
    globalIndexById,
    showStartIdx,
    collapsedGroups,
    collapsingGroups,
    expandingGroups,
    rows,
  };
}
